import { ComponentMovementRequest } from '@paragon/layout-models';
import { enqueueError } from '@paragon/notification-tools';

import { ChooseQuantitiesDialogProps } from '../../components/dialogs/ChooseQuantitiesDialog';
import { ComponentCopyDialogProps } from '../../components/dialogs/ComponentCopyConfirmationDialog';
import { MoveCopyDestinationDialogProps } from '../../components/dialogs/MoveCopyDestinationDialog';
import { ProjectFilesDialogProps } from '../../components/dialogs/ProjectFilesDialog';
import { getScopeComponentDesignGuids, ProjectActionScope, requireSingleScopeProjectGuid } from '../projectActionScope';
import { showPopup } from '../slices/popupSlice';
import { loadHistoriesForComponentDesigns } from './analysisHistory';
import { loadProductionGroupsForProject } from './productionGroups';
import { loadComponentDesignSummaries } from './projects';
import { ThunkActionVoid } from './thunkActions';

export const COMPONENT_MOVEMENT_CONFIRMATION_DIALOG = 'COMPONENT_MOVEMENT_CONFIRMATION';
export const COMPONENT_COPY_CONFIRMATION_DIALOG = 'COMPONENT_COPY_CONFIRMATION';
export const QUANTITIES_DIALOG = 'QUANTITIES';
export const MATERIAL_DETAILS_OPTIONS_DIALOG = 'MATERIAL_DETAILS_OPTIONS_DIALOG';
export const SHOP_DRAWING_OPTIONS_DIALOG = 'SHOP_DRAWING_OPTIONS_DIALOG';
export const ARCHIVE_CONFIRMATION_DIALOG = 'ARCHIVE_CONFIRMATION_DIALOG';
export const CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG = 'CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG';
export const TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG = 'TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG';
export const ADD_MISCELLANEOUS_FILES_DIALOG = 'ADD_MISCELLANEOUS_FILES_DIALOG';
export const CREATE_PRODUCTION_GROUP_DIALOG = 'CREATE_PRODUCTION_GROUP_DIALOG';
export const ADD_TO_PRODUCTION_GROUP_DIALOG = 'ADD_TO_PRODUCTION_GROUP_DIALOG';
export const MANAGE_PRODUCTION_GROUPS_DIALOG = 'MANAGE_PRODUCTION_GROUPS_DIALOG';
export const REPLACE_PRODUCTION_COMPONENTS = 'REPLACE_PRODUCTION_COMPONENTS';
export const PROJECT_FILES_DIALOG = 'PROJECT_FILES_DIALOG';
export const MOVE_COPY_DESTINATION_DIALOG = 'MOVE_COPY_DESTINATION_DIALOG';

export interface PopupOptionsMap {
  [COMPONENT_MOVEMENT_CONFIRMATION_DIALOG]: ComponentMovementRequest;
  [COMPONENT_COPY_CONFIRMATION_DIALOG]: ComponentCopyDialogProps;
  [QUANTITIES_DIALOG]: ChooseQuantitiesDialogProps;
  [MATERIAL_DETAILS_OPTIONS_DIALOG]: { readonly scope: ProjectActionScope };
  [SHOP_DRAWING_OPTIONS_DIALOG]: { readonly scope: ProjectActionScope };
  [ARCHIVE_CONFIRMATION_DIALOG]: { readonly projectGuids: readonly string[] };
  [CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG]: {
    readonly chooseTrussDesignDrawingTemplateSubmitAction: ChooseTrussDesignDrawingTemplateSubmitAction;
    readonly scope: ProjectActionScope;
  };
  [TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG]: { readonly scope: ProjectActionScope };
  [ADD_MISCELLANEOUS_FILES_DIALOG]: { readonly jobId: number; readonly reviewUrl: string };
  [CREATE_PRODUCTION_GROUP_DIALOG]: { readonly scope: ProjectActionScope };
  [ADD_TO_PRODUCTION_GROUP_DIALOG]: { readonly scope: ProjectActionScope };
  [MANAGE_PRODUCTION_GROUPS_DIALOG]: { readonly scope: ProjectActionScope };
  [REPLACE_PRODUCTION_COMPONENTS]: { readonly scope: ProjectActionScope };
  [PROJECT_FILES_DIALOG]: ProjectFilesDialogProps;
  [MOVE_COPY_DESTINATION_DIALOG]: MoveCopyDestinationDialogProps;
}

export function showComponentMovementConfirmationDialog(props: ComponentMovementRequest) {
  return showPopup(COMPONENT_MOVEMENT_CONFIRMATION_DIALOG, props);
}

export function showComponentCopyConfirmationDialog(props: ComponentCopyDialogProps) {
  return showPopup(COMPONENT_COPY_CONFIRMATION_DIALOG, props);
}

export type QuantitiesDialogSubmitAction =
  | 'DownloadCyb'
  | 'DownloadTres'
  | 'DownloadTps'
  | 'DownloadBlade'
  | 'DownloadComponentReport'
  | 'DownloadTrs'
  | 'DownloadTrussDAndE'
  | 'DownloadTrsMachinery'
  | 'DownloadBvn'
  | 'DownloadOmn'
  | 'DownloadLumberPickList'
  | 'DownloadPlatesPickList';

export function showQuantitiesDialog(
  quantitiesDialogSubmitAction: QuantitiesDialogSubmitAction,
  scope: ProjectActionScope,
) {
  return showPopup(QUANTITIES_DIALOG, { quantitiesDialogSubmitAction, scope });
}

export function showMaterialDetailsOptionsDialog(scope: ProjectActionScope) {
  return showPopup(MATERIAL_DETAILS_OPTIONS_DIALOG, { scope });
}

export function showShopDrawingOptionsDialog(scope: ProjectActionScope) {
  return showPopup(SHOP_DRAWING_OPTIONS_DIALOG, { scope });
}

export function showArchiveConfirmationDialog(projectGuids: readonly string[]) {
  return showPopup(ARCHIVE_CONFIRMATION_DIALOG, { projectGuids });
}

export function showProjectFilesDialog(projectGuid: string) {
  return showPopup(PROJECT_FILES_DIALOG, { projectGuid });
}

export function showMoveCopyDestinationDialog(intent: 'copy' | 'move', scope: ProjectActionScope): ThunkActionVoid {
  return function showMoveCopyDestinationDialogThunkAction(dispatch) {
    if (scope.componentDesignPaths.length === 0) {
      dispatch(enqueueError(`Select at least one component to ${intent}`));
      return;
    }

    // copying requires a single source project while moving may gather components from several
    if (intent === 'copy' && requireSingleScopeProjectGuid(scope, dispatch) == null) return;

    dispatch(showPopup(MOVE_COPY_DESTINATION_DIALOG, { intent, scope }));
  };
}

export type ChooseTrussDesignDrawingTemplateSubmitAction = 'RequestSeals' | 'RequestRepair';

export function showChooseTrussDesignDrawingTemplatesDialog(
  chooseTrussDesignDrawingTemplateSubmitAction: ChooseTrussDesignDrawingTemplateSubmitAction,
  scope: ProjectActionScope,
) {
  return showPopup(CHOOSE_TRUSS_DESIGN_DRAWING_TEMPLATES_DIALOG, {
    chooseTrussDesignDrawingTemplateSubmitAction,
    scope,
  });
}

export function showTrussDesignDrawingDownloadDialog(scope: ProjectActionScope) {
  return showPopup(TRUSS_DESIGN_DRAWING_DOWNLOAD_DIALOG, { scope });
}

export function showAddMiscellaneousFilesDialog(jobId: number, reviewUrl: string) {
  return showPopup(ADD_MISCELLANEOUS_FILES_DIALOG, { jobId, reviewUrl });
}

type ProductionGroupDialogType =
  | typeof CREATE_PRODUCTION_GROUP_DIALOG
  | typeof ADD_TO_PRODUCTION_GROUP_DIALOG
  | typeof MANAGE_PRODUCTION_GROUPS_DIALOG
  | typeof REPLACE_PRODUCTION_COMPONENTS;

function makeShowProductionGroupDialog(popupType: ProductionGroupDialogType) {
  return (scope: ProjectActionScope): ThunkActionVoid =>
    function showProductionGroupDialogThunkAction(dispatch) {
      const projectGuid = requireSingleScopeProjectGuid(scope, dispatch);
      if (projectGuid == null) {
        return;
      }

      dispatch(showPopup(popupType, { scope }));
      dispatch(loadDataForProductionGroupDialog(projectGuid, scope));
    };
}

export const showCreateProductionGroupDialog = makeShowProductionGroupDialog(CREATE_PRODUCTION_GROUP_DIALOG);
export const showAddToProductionGroupDialog = makeShowProductionGroupDialog(ADD_TO_PRODUCTION_GROUP_DIALOG);
export const showManageProductionGroupsDialog = makeShowProductionGroupDialog(MANAGE_PRODUCTION_GROUPS_DIALOG);
export const showReplaceProductionComponentsDialog = makeShowProductionGroupDialog(REPLACE_PRODUCTION_COMPONENTS);

function loadDataForProductionGroupDialog(projectGuid: string, scope: ProjectActionScope): ThunkActionVoid {
  return function loadDataForProductionGroupDialogThunkAction(dispatch) {
    const componentDesignGuids = getScopeComponentDesignGuids(scope);

    try {
      const productionGroupsForProjectPromise = dispatch(loadProductionGroupsForProject(projectGuid));

      // Loading the component design summaries gives us the timestamps for the component designs, helping us to identify
      // components with working changes.
      const componentDesignSummariesPromise = dispatch(loadComponentDesignSummaries(componentDesignGuids));

      // Loading the histories gives us access to the analysis set GUIDs associated with a component design.
      const historiesPromise = dispatch(loadHistoriesForComponentDesigns(componentDesignGuids));
      return Promise.all([productionGroupsForProjectPromise, componentDesignSummariesPromise, historiesPromise]);
    } catch (exception) {
      console.error(exception);
      dispatch(enqueueError('Error loading production group data for project'));
    }
  };
}
